import React, {useCallback, useMemo, useEffect, useRef} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import * as S from './Filters.styles';

import Chip from '../../../../../stories/components/hotels/common/Chip/Chip';
import BackToTopCard from '../../../../../stories/components/hotels/SRP/BackToTopCard/BackToTopCard';
import CheckBoxList from '../../../../../stories/components/hotels/common/CheckBoxList/CheckBoxList';
import ToggleButtonList from '../../../../../stories/components/hotels/common/ToggleButtonList/ToggleButtonList';
import FilterLoader from './../../../../../stories/components/hotels/Loadingpage/SrpFilters/SrpFilters';
import SeoLinksList from '../../../../../stories/components/hotels/SRP/SeoLinksList/SeoLinksList';
import {ArrayTogglerBehaviorTypes} from '../../../../hooks/useArrayToggler';
import {
  IUpdate_Filter,
  UPDATE_FILTER,
  IFilters,
  RESET_FILTER,
  UPDATE_SRP_CONFIG,
  ICityMetaInfo,
  IUpdate_Srp_Config,
} from '../../redux/types';
import {userSelector, IUserStoreState} from '@stencil/user-reducer';

import {fetchSrpDataThunk, removeFilterThunk} from '../../redux/thunks';
import {
  hasDataSelectorFn,
  srpDataSelectorFn,
  srpConfigSelectorFn,
  hotelChainListSelectorFn,
  seoFilterLinksFn,
  isRnplFilterSelector,
} from '../../redux/selectors';

import {
  findFixedFilter,
  fixedFilters,
  updateFilterForSingleSelection,
  // fixedPopularFilter,
  getPopularFilterConfig,
} from '../../utils/filter-utils';
import {isObjectEmpty} from '../../../../utils/common-utils';
import RatingStarIcon from '../../../../../stories/Icons/RatingStarIcon';
import {pushga} from 'ga-utils';
import {SrpGAEvents} from '../../../../../shared/config/ga-config';
import {useNavigateForSearchWidget} from '../../../../hooks/useNavigateForSearchWidget';
const Filters = ({isSeo = false}: {isSeo: boolean}) => {
  const dispatch = useDispatch();

  // Note for later: we can batch multiple selector lookups and return multiple instead
  const hasData = useSelector(hasDataSelectorFn);
  const {city_meta_info} = useSelector(srpDataSelectorFn);
  const isRnplFilter = useSelector(isRnplFilterSelector);

  const popularFilterConfig = useMemo(() => getPopularFilterConfig(isRnplFilter), [isRnplFilter]);

  let {filter: appliedFilters, overriderankvhidName, overriderankvhids} = useSelector(
    srpConfigSelectorFn,
  );
  const {onSearch: onFilterChange} = useNavigateForSearchWidget();
  const hotelChainFilterList = useSelector(hotelChainListSelectorFn);
  const filterLinks = useSelector(seoFilterLinksFn);
  const updateFiltersCaller = useCallback(
    (actionData: IUpdate_Filter['actionData'], showFilterOverlay = true) => {
      onFilterChange({newFilterData: actionData, resetAllData: false, showOverlayLoader: true});
    },
    [dispatch],
  );

  const updateSrpConfigCaller = useCallback(
    (actionData: IUpdate_Srp_Config['actionData']) => {
      dispatch({type: UPDATE_SRP_CONFIG, actionData});
      dispatch(fetchSrpDataThunk());
    },
    [dispatch],
  );

  const resetFiltersCaller = useCallback(() => {
    dispatch({type: RESET_FILTER});
    // dispatch(fetchSrpDataThunk());
    onFilterChange({resetAllData: false});
  }, [dispatch]);

  const removeFilterCaller = useCallback(
    (filterTarget, value = null) => {
      dispatch(removeFilterThunk(filterTarget, onFilterChange));
      // dispatch(fetchSrpDataThunk());
    },
    [dispatch],
  );

  if (hasData === null) return <FilterLoader />;
  // if (isSrpApiLoading) return <FilterLoader />;

  let tabsList = [];
  let hotelTypeFiltersList = [];
  let amenitiesFiltersList = [];

  if (city_meta_info) {
    const {flt, qf, aty, am} = city_meta_info as ICityMetaInfo;
    tabsList = flt;
    hotelTypeFiltersList = aty;
    amenitiesFiltersList = am;
  }

  let {
    type: hotelTypeFilterListInState,
    chain: hotelChainFilterListInState,
    amenities: amenitiesFilterListInState,
    step_range: priceRangeFilterInState,
    range_ugc: customerRatingFilterInState,
    star: starRatingFilterInState,
  } = appliedFilters as IFilters;

  const listOfFilterTypesToSkipInAppliedFiltersDisplayList = ['pcf', 'lid', 'tag', 'hctx'];

  const renderChipList = () => {
    if (overriderankvhidName) {
      appliedFilters = {...appliedFilters, overriderankvhidName};
    }

    if (isObjectEmpty(appliedFilters)) return null;

    if (tabsList && tabsList.length > 0) {
      const tabsListItemIsPresent = tabsList.find(
        item => item.fd && Object.keys(item.fd).length > 0,
      );
      if (tabsListItemIsPresent)
        listOfFilterTypesToSkipInAppliedFiltersDisplayList.push(
          Object.keys(tabsListItemIsPresent.fd)[0],
        );
    }

    return Object.keys(appliedFilters)
      .filter(
        appliedFiltersKeyName =>
          listOfFilterTypesToSkipInAppliedFiltersDisplayList.indexOf(appliedFiltersKeyName) === -1,
      )
      .map(itemKey => {
        if (appliedFilters[itemKey] && appliedFilters[itemKey].length > 0) {
          // FIXED FILTER HANDLING ~ can be improved
          if (['step_range', 'star', 'range_ugc'].indexOf(itemKey) > -1) {
            if (itemKey === 'range_ugc') {
              return (
                <Chip
                  key={itemKey + 'appliedFilterChipChild'}
                  text={
                    findFixedFilter({
                      src: fixedFilters[itemKey].data,
                      target: appliedFilters[itemKey],
                      type: itemKey,
                    }).name
                  }
                  actionHandler={() => {
                    removeFilterCaller(itemKey, appliedFilters[itemKey]);
                  }}
                />
              );
            }

            return appliedFilters[itemKey].map((item, cindex) => {
              return (
                <Chip
                  key={cindex + 'appliedFilterChipChild'}
                  text={
                    findFixedFilter({
                      src: fixedFilters[itemKey].data,
                      target: item,
                      type: itemKey,
                    }).name
                  }
                  actionHandler={() => {
                    removeFilterCaller(itemKey, item);
                  }}
                />
              );
            });
          }

          // REST OF THE FILTERS HANDLING
          if (Array.isArray(appliedFilters[itemKey])) {
            return appliedFilters[itemKey].map((item, cindex) => {
              const isLocationSearch = item.id_type && item.id_type === 'voy';
              const isQuickFilter =
                ['location', 'landmark', 'transit', 'location_search', 'utag'].indexOf(itemKey) >
                -1;
              const isKnownHermesFilter = ['amenities', 'chain', 'type'].indexOf(itemKey) > -1;
              let text = item;
              let arg = {};
              if (isLocationSearch) {
                text = item.n;
                arg[itemKey] = appliedFilters[itemKey].filter(
                  appliedFilterItem =>
                    appliedFilterItem.loc_id && appliedFilterItem.loc_id !== item.loc_id,
                );
              }

              if (isQuickFilter) {
                text = item.n;
                arg[itemKey] = appliedFilters[itemKey].filter(
                  appliedFilterItem =>
                    appliedFilterItem.loc_id && appliedFilterItem.loc_id !== item.loc_id,
                );
              }

              if (isKnownHermesFilter) {
                arg[itemKey] = appliedFilters[itemKey].filter(
                  appliedFilterItem => appliedFilterItem !== item,
                );
              }

              return (
                <Chip
                  key={cindex + 'appliedFilterChipChild'}
                  text={text}
                  actionHandler={() => {
                    updateFiltersCaller(arg, false);
                  }}
                />
              );
            });
          }

          let arg = {};
          arg[itemKey] = '';

          if (itemKey === 'pc') {
            arg = {...arg, pcf: 0};
          }

          if (itemKey === 'hctxTitle') {
            arg = {...arg, hctx: ''};
          }

          return (
            <Chip
              key={itemKey + 'appliedFilterChipChild'}
              text={appliedFilters[itemKey]}
              actionHandler={() => {
                itemKey === 'overriderankvhidName'
                  ? updateSrpConfigCaller({
                      overriderankvhids: null,
                      overriderankvhidName: '',
                    })
                  : updateFiltersCaller(arg);
              }}
            />
          );
        }
      });
  };

  const hasAppliedFilter =
    appliedFilters &&
    Object.keys(appliedFilters).length > 0 &&
    Object.keys(appliedFilters).find(itemKey => {
      if (listOfFilterTypesToSkipInAppliedFiltersDisplayList.indexOf(itemKey) > -1) return false;

      if (Array.isArray(appliedFilters[itemKey])) {
        return appliedFilters[itemKey].length > 0;
      }

      return !!appliedFilters[itemKey];
    });

  const gaHandler = (action, label) => {
    pushga({
      eventCategory: SrpGAEvents.hotelSrpFilterClicked,
      eventAction: action,
      eventLabel: label,
    });
  };

  return (
    <>
      <S.FiltersWrapperDiv>
        <S.FiltersInfoBlockWrapperDiv>
          <S.FiltersHeaderBlockDiv>
            {/* <div> */}
            <S.FiltersHeadingTextWrapperH3>FILTERS</S.FiltersHeadingTextWrapperH3>
            {/* </div> */}
            {/* <div> */}
            <S.ClearTextSpan
              isactive={hasAppliedFilter ? 'true' : 'false'}
              onClick={() => {
                resetFiltersCaller();
                gaHandler('Clear all', '');
              }}
            >
              CLEAR
            </S.ClearTextSpan>
            {/* </div> */}
          </S.FiltersHeaderBlockDiv>

          <S.AppliedFiltersWrapperDiv>{renderChipList()}</S.AppliedFiltersWrapperDiv>
        </S.FiltersInfoBlockWrapperDiv>

        <ToggleButtonList
          name="Popular Filters"
          data={popularFilterConfig.data}
          lookup={item => item.n}
          type={ArrayTogglerBehaviorTypes.multiSelect}
          renderItem={item => <>{item.n}</>}
          actionHandler={(_, itemUnderAction, isSelected) => {
            updateFiltersCaller(updateFilterForSingleSelection(itemUnderAction.fd, appliedFilters));
            gaHandler(`PaM: ${itemUnderAction?.n}`, isSelected);
          }}
          defaultState={Object.keys(appliedFilters)
            .filter(
              item => popularFilterConfig.symbols.indexOf(item) > -1 && appliedFilters[item] === 1,
            )
            .map(item => {
              let arg = {};
              arg[item] = appliedFilters[item];
              return {n: popularFilterConfig.names[item], fd: {...arg}};
            })}
          resetHandlerProp={() => {
            updateFiltersCaller(popularFilterConfig.resetValues);
          }}
        />

        <CheckBoxList
          name="Price Range"
          data={fixedFilters.step_range.data}
          lookup={item => `${fixedFilters.step_range.name}${item.key.min}`}
          type={fixedFilters.step_range.type}
          renderItem={item => <>{item.name}</>}
          actionHandler={(item, itemUnderAction, isSelected) => {
            updateFiltersCaller({step_range: item.map(subItem => subItem.key)});
            gaHandler(`Price: ${itemUnderAction?.name}`, isSelected);
          }}
          resetHandlerProp={() => {
            updateFiltersCaller({step_range: []});
          }}
          defaultState={
            (priceRangeFilterInState &&
              priceRangeFilterInState.map(item =>
                findFixedFilter({
                  target: item,
                  src: fixedFilters.step_range.data,
                  type: fixedFilters.step_range.name,
                }),
              )) ||
            []
          }
        />

        <CheckBoxList
          name="Customer Ratings"
          data={fixedFilters.range_ugc.data}
          lookup={item => `${fixedFilters.range_ugc.name}${item.key[0]}`}
          type={fixedFilters.range_ugc.type}
          renderItem={item => (
            <>
              <S.AverageReviewWrapper ratingCount={item.key[0]}>
                <S.AverageReviewText>{item.key[0]}+ </S.AverageReviewText>
              </S.AverageReviewWrapper>
            </>
          )}
          actionHandler={(item, itemUnderAction, isSelected) => {
            updateFiltersCaller({range_ugc: (item.length && item[0].key) || []});
            gaHandler(`GiR: ${itemUnderAction?.name}`, isSelected);
          }}
          resetHandlerProp={() => {
            updateFiltersCaller({range_ugc: []});
          }}
          defaultState={
            (customerRatingFilterInState &&
              customerRatingFilterInState.length > 0 && [
                findFixedFilter({
                  target: customerRatingFilterInState,
                  src: fixedFilters.range_ugc.data,
                  type: fixedFilters.range_ugc.name,
                }),
              ]) ||
            []
          }
        />

        <CheckBoxList
          name="Star Rating"
          data={fixedFilters.star.data}
          lookup={item => `${fixedFilters.star.name}${item.key}`}
          type={fixedFilters.star.type}
          renderItem={item => (
            <S.StarRatingWrapperSpan>
              {[...Array(item.key)].map((item, index) => (
                <RatingStarIcon key={'starIcon' + index} />
              ))}
            </S.StarRatingWrapperSpan>
          )}
          actionHandler={(item, itemUnderAction, isSelected) => {
            updateFiltersCaller({star: item.map(subItem => subItem.key)});
            gaHandler(`StR: ${itemUnderAction?.name}`, isSelected);
          }}
          resetHandlerProp={() => {
            updateFiltersCaller({star: []});
          }}
          defaultState={
            (starRatingFilterInState &&
              starRatingFilterInState.map(item =>
                findFixedFilter({
                  target: item,
                  src: fixedFilters.star.data,
                  type: fixedFilters.star.name,
                }),
              )) ||
            []
          }
          maxLength={3}
        />

        {hotelTypeFiltersList && hotelTypeFiltersList.length > 0 && (
          <CheckBoxList
            name="Hotel Type"
            data={hotelTypeFiltersList}
            lookup={item => item}
            type={ArrayTogglerBehaviorTypes.multiSelect}
            renderItem={item => <S.TextTruncatorSpan>{item}</S.TextTruncatorSpan>}
            actionHandler={(item: IFilters['type'], itemUnderAction, isSelected) => {
              updateFiltersCaller({type: item});
              gaHandler(`PropType: ${itemUnderAction}`, isSelected);
            }}
            defaultState={hotelTypeFilterListInState || []}
            resetHandlerProp={() => {
              updateFiltersCaller({type: []});
            }}
            maxLength={3}
          />
        )}

        {amenitiesFiltersList && amenitiesFiltersList.length > 0 && (
          <CheckBoxList
            name="Amenities"
            data={amenitiesFiltersList}
            lookup={item => item}
            type={ArrayTogglerBehaviorTypes.multiSelect}
            renderItem={item => <S.TextTruncatorSpan>{item}</S.TextTruncatorSpan>}
            actionHandler={(item: IFilters['amenities'], itemUnderAction, isSelected) => {
              updateFiltersCaller({amenities: item});
              gaHandler(`Amenities: ${itemUnderAction}`, isSelected);
            }}
            defaultState={amenitiesFilterListInState || []}
            resetHandlerProp={() => {
              updateFiltersCaller({amenities: []});
            }}
            maxLength={3}
          />
        )}

        {/* {hotelChainFilterList && hotelChainFilterList.length > 0 && (
            <CheckBoxList
              name="Hotel Chain"
              data={hotelChainFilterList}
              lookup={item => item}
              type={ArrayTogglerBehaviorTypes.multiSelect}
              renderItem={item => <S.TextTruncatorSpan>{item}</S.TextTruncatorSpan>}
              actionHandler={(item, itemUnderAction, isSelected) => {
                updateFiltersCaller({chain: item.map(chainItem => chainItem)});
                gaHandler(`HotelChain: ${itemUnderAction}`, isSelected);
              }}
              defaultState={hotelChainFilterListInState || []}
              resetHandlerProp={() => {
                updateFiltersCaller({chain: []});
              }}
              maxLength={3}
            />
          )} */}
      </S.FiltersWrapperDiv>
      <BackToTopCard />
    </>
  );
};

export default React.memo(Filters);
